/*
  DAC Carousel — <dac-carousel> custom element (no dependencies).

  Drives all three layouts of blocks/dac-carousel.liquid:

    standard  native scroll-snap; the element only reads scroll position,
              drives the arrows/dots, and scrolls on demand. Touch swipe,
              momentum and RTL come free from the browser.
    circular  every slide absolutely stacked at centre, then fanned out by
              index distance from the active one (translate + scale + arc).
    stacked   same absolute stage, but the fan runs one way only, so the
              cards peek out from the chosen side.

  Slides are the DIRECT CHILDREN of the track. Inside a Shopify theme block
  those are the <div id="shopify-block-…"> wrappers that content_for 'blocks'
  emits, so nothing here assumes a class of ours on the slide itself.

  Everything tunable is read from CSS custom properties on the root element,
  which the block writes per breakpoint. That keeps responsive behaviour in
  CSS and lets this file stay layout-agnostic.
*/
(function () {
  if (customElements.get('dac-carousel')) return;

  var REDUCED_MOTION =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function num(value, fallback) {
    var parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  /*
    How large a card `distance` steps back from the active one is drawn. The
    floor keeps a card that has shrunk past nothing from inverting.
  */
  function cardScale(distance, scaleStep) {
    return Math.max(0.1, 1 - distance * (scaleStep / 100));
  }

  class DacCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.root = this.querySelector('.dac-carousel') || this;
      this.track = this.querySelector('[data-dac-car-track]');
      if (!this.track) return;

      this.slides = Array.prototype.filter.call(this.track.children, function (el) {
        return el.nodeType === 1;
      });
      if (!this.slides.length) return;

      this.layout = this.root.getAttribute('data-layout') || 'standard';
      this.loop = this.root.getAttribute('data-loop') === 'true';
      this.draggable = this.root.getAttribute('data-draggable') === 'true';
      this.autoplay = this.root.getAttribute('data-autoplay') === 'true' && !REDUCED_MOTION;
      this.autoplayDelay = num(this.root.getAttribute('data-autoplay-delay'), 5000);
      this.pauseOnHover = this.root.getAttribute('data-pause-on-hover') !== 'false';

      this.arrowButtons = Array.prototype.slice.call(
        this.querySelectorAll('[data-dac-car-prev], [data-dac-car-next]')
      );
      this.dotContainers = Array.prototype.slice.call(this.querySelectorAll('[data-dac-car-dots]'));
      // "Maximum dots": 0 (attribute absent) shows one dot per slide, as before.
      this.maxDots = num(this.root.getAttribute('data-dots-max'), 0);

      this.index = clamp(num(this.root.getAttribute('data-start'), 1) - 1, 0, this.slides.length - 1);
      this.pageOffsets = [];

      // Containers elsewhere on the page whose children pair with the slides.
      this.panelClass = (this.root.getAttribute('data-panel-class') || '').replace(/^\./, '').trim();
      this.panelGroups = [];
      this.cardAlign = this.root.getAttribute('data-card-align') || 'center';
      this.navLocation = this.root.getAttribute('data-nav-location') || 'carousel';
      this.navMounted = false;

      this.slides.forEach(function (slide, i) {
        slide.setAttribute('data-dac-car-index', String(i));
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', 'Slide ' + (i + 1) + ' of ' + this.slides.length);
      }, this);

      this.bindControls();
      this.bindPointer();
      this.bindKeyboard();
      this.bindEditor();
      this.observeResize();

      /*
        Measure immediately, then again on the next frame and once everything
        has loaded. The first pass is what makes the carousel usable even when
        no animation frame is delivered (a section rendered in a background
        tab, or an editor re-render); the later passes correct for nested
        blocks whose boxes change as images, fonts and aspect ratios settle.
      */
      var self = this;
      this.refresh();
      requestAnimationFrame(function () {
        self.refresh();
      });
      if (document.readyState !== 'complete') {
        window.addEventListener(
          'load',
          function () {
            self.refresh();
          },
          { once: true }
        );
      }

      if (this.autoplay) this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.onDocumentSelect) {
        document.removeEventListener('shopify:block:select', this.onDocumentSelect);
        document.removeEventListener('shopify:block:select', this.onPanelSelect);
        document.removeEventListener('shopify:block:deselect', this.onDocumentDeselect);
      }
    }

    /* ------------------------------------------------------------ wiring */

    bindControls() {
      var self = this;

      this.arrowButtons.forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          self.pauseAutoplayForInteraction();
          if (button.hasAttribute('data-dac-car-prev')) self.prev();
          else self.next();
        });
      });

      // Clicking a card that is not the active one brings it to the front.
      if (this.layout !== 'standard') {
        this.slides.forEach(function (slide, i) {
          slide.addEventListener('click', function (event) {
            if (self.index === i) return;
            if (self.dragMoved) return;
            // A click on a real link/button inside a background card would be
            // ambiguous, so bring the card forward instead of following it.
            event.preventDefault();
            self.pauseAutoplayForInteraction();
            self.goTo(i);
          });
        });
      }

      if (this.pauseOnHover && this.autoplay) {
        this.addEventListener('mouseenter', function () {
          self.stopAutoplay();
        });
        this.addEventListener('mouseleave', function () {
          self.startAutoplay();
        });
        this.addEventListener('focusin', function () {
          self.stopAutoplay();
        });
        this.addEventListener('focusout', function () {
          if (!self.contains(document.activeElement)) self.startAutoplay();
        });
      }

      if (this.layout === 'standard') {
        var scrollTick = null;
        this.track.addEventListener(
          'scroll',
          function () {
            if (scrollTick) return;
            scrollTick = requestAnimationFrame(function () {
              scrollTick = null;
              self.syncIndexFromScroll();
            });
          },
          { passive: true }
        );
      }
    }

    bindKeyboard() {
      var self = this;
      this.track.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        // Let a focused text input keep its own arrow-key behaviour.
        var tag = document.activeElement && document.activeElement.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        event.preventDefault();
        self.pauseAutoplayForInteraction();
        if (event.key === 'ArrowLeft') self.prev();
        else self.next();
      });
    }

    /*
      Drag/swipe. The standard layout is a native scroll container, so the
      browser already handles touch; only the absolutely-positioned layouts
      need a pointer handler, and there a horizontal drag past the threshold
      steps one slide.
    */
    bindPointer() {
      if (!this.draggable || this.layout === 'standard') return;

      var self = this;
      var startX = 0;
      var startY = 0;
      var active = false;

      this.track.addEventListener(
        'pointerdown',
        function (event) {
          if (event.button != null && event.button !== 0) return;
          active = true;
          self.dragMoved = false;
          startX = event.clientX;
          startY = event.clientY;
        },
        { passive: true }
      );

      this.track.addEventListener(
        'pointermove',
        function (event) {
          if (!active) return;
          var dx = event.clientX - startX;
          var dy = event.clientY - startY;
          // A mostly-vertical drag is the page scrolling, not a swipe.
          if (Math.abs(dy) > Math.abs(dx)) return;
          if (Math.abs(dx) < 40) return;

          active = false;
          self.dragMoved = true;
          self.pauseAutoplayForInteraction();
          if (dx < 0) self.next();
          else self.prev();
        },
        { passive: true }
      );

      var end = function () {
        active = false;
        // Clear on the next tick so the click handler above can still see it.
        setTimeout(function () {
          self.dragMoved = false;
        }, 0);
      };

      this.track.addEventListener('pointerup', end, { passive: true });
      this.track.addEventListener('pointercancel', end, { passive: true });
      this.track.addEventListener('pointerleave', end, { passive: true });
    }

    /*
      Theme editor: selecting a slide block in the sidebar should bring that
      slide into view, and autoplay should hold still while it is selected.
    */
    bindEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      var self = this;

      this.onDocumentSelect = function (event) {
        var target = event.target;
        if (!target || !self.contains(target)) return;
        self.stopAutoplay();
        var slide = self.slides.indexOf(target);
        if (slide === -1) {
          // The event fired on a block nested inside a slide.
          for (var i = 0; i < self.slides.length; i++) {
            if (self.slides[i].contains(target)) {
              slide = i;
              break;
            }
          }
        }
        if (slide > -1) self.goTo(slide);
      };

      /*
        Selecting a linked panel in the sidebar should bring its slide forward
        too — otherwise editing panel 3 means staring at a hidden block. The
        panel groups live outside this element, so this listener is separate.
      */
      this.onPanelSelect = function (event) {
        if (!self.panelClass || !self.panelGroups.length) return;
        var target = event.target;
        if (!target) return;
        self.panelGroups.forEach(function (group) {
          if (!group.contains(target)) return;
          var panels = self.panelsIn(group);
          for (var i = 0; i < panels.length; i++) {
            if (panels[i] === target || panels[i].contains(target)) {
              self.stopAutoplay();
              self.goTo(i);
              return;
            }
          }
        });
      };

      this.onDocumentDeselect = function () {
        if (self.autoplay) self.startAutoplay();
      };

      document.addEventListener('shopify:block:select', this.onDocumentSelect);
      document.addEventListener('shopify:block:select', this.onPanelSelect);
      document.addEventListener('shopify:block:deselect', this.onDocumentDeselect);
    }

    observeResize() {
      var self = this;
      if (!('ResizeObserver' in window)) {
        window.addEventListener('resize', function () {
          self.refresh();
        });
        return;
      }

      var frame = null;
      this.resizeObserver = new ResizeObserver(function () {
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(function () {
          frame = null;
          self.refresh();
        });
      });

      this.resizeObserver.observe(this);
      // Card height drives the stage height on the absolute layouts.
      this.slides.forEach(function (slide) {
        self.resizeObserver.observe(slide);
      });
    }

    /* ---------------------------------------------------------- movement */

    /*
      Slides per arrow click. The block writes --dac-car-step per breakpoint;
      0 means a full page, i.e. however many slides --dac-car-per shows right
      now. The transform layouts only ever bring one card forward at a time.
    */
    stepSize() {
      if (this.layout !== 'standard') return 1;
      var styles = getComputedStyle(this.root);
      var step = Math.floor(num(styles.getPropertyValue('--dac-car-step'), 1));
      if (step <= 0) step = Math.floor(num(styles.getPropertyValue('--dac-car-per'), 1));
      return Math.max(1, step);
    }

    /*
      A step that would overshoot the end lands exactly on the end first, so
      the last slides are never skipped; only a step taken from the end itself
      wraps (when looping). prev() mirrors this at the start.
    */
    next() {
      var last = this.lastIndex();
      if (this.index >= last) {
        this.goTo(this.index + 1);
        return;
      }
      this.goTo(Math.min(this.index + this.stepSize(), last));
    }

    prev() {
      if (this.index <= 0) {
        this.goTo(this.index - 1);
        return;
      }
      this.goTo(Math.max(this.index - this.stepSize(), 0));
    }

    goTo(target) {
      var last = this.lastIndex();
      var index = target;

      if (index > last) index = this.loop ? 0 : last;
      if (index < 0) index = this.loop ? last : 0;

      this.index = index;

      if (this.layout === 'standard') this.scrollToIndex(index);
      else this.renderStage();

      this.updateControls();
    }

    /*
      On the standard layout the reachable positions are the slide offsets
      clamped to the container's maximum scroll — with 3-up, the last two
      slides share the final position, so they must share a dot too.
    */
    lastIndex() {
      if (this.layout !== 'standard') return this.slides.length - 1;
      if (!this.pageOffsets.length) return this.slides.length - 1;
      return this.pageOffsets.length - 1;
    }

    measurePages() {
      if (this.layout !== 'standard') {
        this.pageOffsets = this.slides.map(function (_, i) {
          return i;
        });
        return;
      }

      var track = this.track;
      var maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      var offsets = [];

      this.slides.forEach(function (slide) {
        var offset = Math.min(slide.offsetLeft - track.offsetLeft, maxScroll);
        // Slides that land on an already-reachable position (the tail of a
        // multi-up row) collapse into the same page.
        var duplicate = offsets.some(function (existing) {
          return Math.abs(existing - offset) < 2;
        });
        if (!duplicate) offsets.push(offset);
      });

      this.pageOffsets = offsets.length ? offsets : [0];
    }

    scrollToIndex(index, behavior) {
      var offset = this.pageOffsets[index];
      if (offset == null) return;
      this.track.scrollTo({
        left: offset,
        behavior: REDUCED_MOTION ? 'auto' : behavior || 'smooth',
      });
    }

    syncIndexFromScroll() {
      if (!this.pageOffsets.length) return;
      var current = this.track.scrollLeft;
      var closest = 0;
      var smallest = Infinity;

      this.pageOffsets.forEach(function (offset, i) {
        var distance = Math.abs(offset - current);
        if (distance < smallest) {
          smallest = distance;
          closest = i;
        }
      });

      if (closest === this.index) return;
      this.index = closest;
      this.updateControls();
    }

    /* ------------------------------------------------- circular / stacked */

    readVars() {
      var styles = getComputedStyle(this.root);
      return {
        spread: num(styles.getPropertyValue('--dac-car-spread'), 55),
        scaleStep: num(styles.getPropertyValue('--dac-car-scale-step'), 15),
        arc: num(styles.getPropertyValue('--dac-car-arc'), 0),
        depth: Math.max(1, num(styles.getPropertyValue('--dac-car-depth'), 2)),
        fade: num(styles.getPropertyValue('--dac-car-fade'), 30),
        rotate: num(styles.getPropertyValue('--dac-car-rotate'), 0),
        stagePad: num(styles.getPropertyValue('--dac-car-stage-pad'), 0),
        stageMax: num(styles.getPropertyValue('--dac-car-stage-max'), 0),
        stackOffset: num(styles.getPropertyValue('--dac-car-stack-offset'), 28),
        side: (styles.getPropertyValue('--dac-car-stack-side') || 'left').trim(),
      };
    }

    /*
      Signed distance from the active slide. With looping on, a slide near the
      end of the list is "one step back" from the first slide rather than
      count-1 steps forward, which is what keeps the fan symmetric as it wraps.
    */
    offsetFor(i) {
      var count = this.slides.length;
      var raw = i - this.index;
      if (!this.loop) return raw;
      if (raw > count / 2) raw -= count;
      if (raw < -count / 2) raw += count;
      return raw;
    }

    renderStage() {
      if (this.layout === 'standard') return;

      var vars = this.readVars();
      var itemWidth = this.slides[0] ? this.slides[0].offsetWidth : 0;
      var count = this.slides.length;
      var self = this;

      /*
        How many steps out the fan can actually reach. `depth` is a cap, not a
        count, and sizing the stage to it reserves room for steps that no card
        can ever occupy — which is what leaves an empty band above and below the
        cards that no padding setting can take back.

        A looping circular fan splits the list either side of the active card,
        so nothing is ever more than half of it away. Every other case runs the
        cards out in one direction, where the furthest is simply the last one.
      */
      var circularWrap = this.layout === 'circular' && this.loop;
      var reachSteps = Math.min(vars.depth, circularWrap ? Math.floor(count / 2) : count - 1);

      var verticalReach = 0;
      var fanDown = false;
      if (this.layout === 'circular') {
        verticalReach = Math.abs(vars.arc) * reachSteps;
        fanDown = vars.arc > 0;
      } else if (vars.side === 'top' || vars.side === 'bottom') {
        verticalReach = vars.stackOffset * reachSteps;
        fanDown = vars.side === 'bottom';
      }

      /*
        Stage height is the tallest card plus the room the fan and the padding
        claim above and below it. A max height caps that whole total, so what is
        left for the cards themselves is the cap minus what the fan and padding
        already spend.

        Only the cards that exceed that are scaled down, and each by its own
        amount — scaling the arrangement as a whole would shrink the empty space
        along with the cards and leave the stage looking exactly as tall. Doing
        it per card lets the stage close up to the height asked for, and the
        card in front resizes to match instead of being cropped.
      */
      var cardCap =
        vars.stageMax > 0 ? Math.max(1, vars.stageMax - verticalReach - vars.stagePad * 2) : 0;
      var fits = [];
      var tallest = 0;
      this.slides.forEach(function (slide, i) {
        var height = slide.offsetHeight;
        fits[i] = cardCap > 0 && height > cardCap ? cardCap / height : 1;
        tallest = Math.max(tallest, height * fits[i]);
      });

      /*
        Writing the stage height resizes this element, which the ResizeObserver
        then reports — so only write it when it actually changed, or the two
        keep waking each other up.
      */
      if (tallest > 0) {
        var stageHeight = Math.ceil(tallest + verticalReach + vars.stagePad * 2);
        if (stageHeight !== this.stageHeight) {
          this.stageHeight = stageHeight;
          this.root.style.setProperty('--dac-car-stage-h', stageHeight + 'px');
        }
      }

      var stackOffset = vars.stackOffset;
      var align = this.cardAlign;

      /*
        Circular spacing has to be cumulative rather than linear. Every card is
        scaled down one step further than the one in front of it, so stepping
        out by a fixed fraction of the *full* card width crowds the big cards at
        the front together and strands the small ones at the back — the gap
        between edges grows with every step even though the centres are evenly
        spread. Stepping instead by that fraction of each adjacent pair's own
        scaled widths keeps the gap proportional the whole way out.
      */
      var spacing = [0];
      if (this.layout === 'circular') {
        var unit = itemWidth * (vars.spread / 100);
        for (var d = 1; d <= count; d++) {
          spacing[d] =
            spacing[d - 1] +
            (unit * (cardScale(d - 1, vars.scaleStep) + cardScale(d, vars.scaleStep))) / 2;
        }
      }

      /*
        A left/right stack fans one way only, so centring it on its active card
        parks the whole peek's width of dead space on the opposite side of the
        stage. Shifting every card by half the reach centres the stack as a
        whole instead, which is also what leaves the cards room to grow.
      */
      var horizontalReach = 0;
      if (this.layout === 'stacked' && vars.side !== 'top' && vars.side !== 'bottom') {
        horizontalReach = stackOffset * reachSteps;
      }
      var stackShift = vars.side === 'right' ? -horizontalReach / 2 : horizontalReach / 2;

      this.slides.forEach(function (slide, i) {
        var offset = self.offsetFor(i);
        var distance = Math.abs(offset);
        var beyond = distance > vars.depth;

        var x = 0;
        // How far below the active card this one fans out; negative is above.
        var drop = 0;
        var scale = 1;
        var rotate = 0;
        var opacity = 1;
        var depthIndex = count - distance;

        if (self.layout === 'circular') {
          x = offset < 0 ? -spacing[distance] : spacing[distance];
          drop = distance * vars.arc;
          scale = cardScale(distance, vars.scaleStep) * fits[i];
          rotate = offset * vars.rotate;
          opacity = Math.max(0, 1 - distance * (vars.fade / 100));
        } else {
          /*
            Stacked: only the cards behind the active one are shown, always on
            the chosen side. Cards already passed sit at the same place as the
            deepest one so the stack never looks lopsided while it advances.
          */
          var behind = self.loop ? (i - self.index + count) % count : Math.max(0, i - self.index);
          var passed = !self.loop && i < self.index;
          var step = Math.min(behind, vars.depth);

          beyond = behind > vars.depth || passed;
          scale = cardScale(step, vars.scaleStep) * fits[i];
          rotate = step * vars.rotate;
          opacity = Math.max(0, 1 - step * (vars.fade / 100));
          depthIndex = count - behind;

          switch (vars.side) {
            case 'right':
              x = step * stackOffset + stackShift;
              break;
            case 'top':
              x = 0;
              drop = -step * stackOffset;
              break;
            case 'bottom':
              x = 0;
              drop = step * stackOffset;
              break;
            default:
              x = -step * stackOffset + stackShift;
          }
        }

        /*
          Anchor the fan. The stage is only verticalReach taller than the cards
          themselves, and all of that slack has to be spent on the side the fan
          runs to — above the cards when they rise, below when they fall — or
          the outermost card lands outside the stage and gets clipped.

          Which edge holds still is the alignment. Top and bottom translate from
          that edge; centre offsets from the stage's middle and so still owes
          the -50% the stylesheet's `top: 50%` is waiting for.
        */
        var y;
        if (align === 'bottom') {
          y = drop - (fanDown ? verticalReach : 0) - vars.stagePad;
        } else if (align === 'center') {
          y = drop + (fanDown ? -verticalReach / 2 : verticalReach / 2);
        } else {
          y = drop + (fanDown ? 0 : verticalReach) + vars.stagePad;
        }

        var yCss =
          align === 'center' ? 'calc(-50% + ' + Math.round(y) + 'px)' : Math.round(y) + 'px';

        slide.style.zIndex = String(beyond ? 0 : depthIndex);
        slide.style.opacity = beyond ? '0' : String(opacity);
        slide.style.transform =
          'translate(calc(-50% + ' +
          Math.round(x) +
          'px), ' +
          yCss +
          ') scale(' +
          scale.toFixed(3) +
          ')' +
          (rotate ? ' rotate(' + rotate.toFixed(2) + 'deg)' : '');

        if (beyond) {
          slide.setAttribute('data-dac-car-hidden', '');
          slide.setAttribute('aria-hidden', 'true');
          slide.inert = true;
        } else {
          slide.removeAttribute('data-dac-car-hidden');
          slide.removeAttribute('aria-hidden');
          slide.inert = false;
        }

        if (i === self.index) slide.setAttribute('data-dac-car-active', '');
        else slide.removeAttribute('data-dac-car-active');
      });
    }

    /* ----------------------------------------------------------- chrome */

    /*
      Navigation on the panels instead of the carousel.

      The bars are authored inside <dac-carousel> either way — that is where
      the buttons get their click handlers, and moving an element does not
      disturb listeners already bound to it — so this only re-parents them.
      Each bar lands in a host that repeats the carousel's data attributes and
      block id, because every nav rule in the stylesheet is written as
      `.dac-carousel[data-arrows-desktop="…"] .dac-carousel__bar…` and the
      block's own <style> keys the custom properties off the block id. Repeat
      both and the relocated chrome styles exactly as it did in place.

      Runs from refresh(), so a panel group rendered by a later section is
      still picked up on a subsequent pass.
    */
    mountNav() {
      if (this.navMounted || this.navLocation !== 'panels') return;

      var groups = this.resolvePanelGroups();
      if (!groups.length) return;

      // Chrome is a single set of elements, so it can only live in one group.
      var group = groups[0];
      var top = this.querySelector('.dac-carousel__bar--top');
      var bottom = this.querySelector('.dac-carousel__bar--bottom');
      var sides = this.querySelector('.dac-carousel__arrows--sides');

      this.navMounted = true;
      if (!top && !bottom && !sides) return;

      group.classList.add('dac-carousel-panel-group');

      if (top) group.insertBefore(this.navHost(top, 'top'), group.firstChild);
      if (bottom) group.appendChild(this.navHost(bottom, 'bottom'));
      if (sides) group.appendChild(this.navHost(sides, 'sides'));
    }

    navHost(nav, place) {
      var host = document.createElement('div');
      var root = this.root;

      host.className = 'dac-carousel dac-carousel--nav-host dac-carousel--nav-host-' + place;
      [
        'data-block-id',
        'data-arrows-desktop',
        'data-arrows-mobile',
        'data-dots-desktop',
        'data-dots-mobile',
        'data-dot-style',
      ].forEach(function (name) {
        var value = root.getAttribute(name);
        if (value !== null) host.setAttribute(name, value);
      });

      /*
        Out here there is no track for side arrows to flank, so they always
        overlay the panel group's edges — the "outside" mode has nothing to sit
        outside of.
      */
      host.setAttribute(
        'data-arrows-inside',
        place === 'sides' ? 'true' : root.getAttribute('data-arrows-inside') || 'true'
      );

      host.appendChild(nav);
      return host;
    }

    /*
      Re-measure after a resize (or the first layout pass) and put the current
      slide back where it belongs. The scroll listener is what keeps `index`
      current between refreshes, so this only ever re-aligns — it never guesses
      a new index, which is what would otherwise throw away "Start on slide".
    */
    refresh() {
      this.measurePages();
      this.mountNav();
      if (this.index > this.lastIndex()) this.index = this.lastIndex();
      if (this.layout === 'standard') {
        this.scrollToIndex(this.index, 'auto');
      } else {
        this.renderStage();
      }
      this.buildDots();
      this.updateControls();
    }

    buildDots() {
      if (!this.dotContainers.length) return;
      var wanted = this.lastIndex() + 1;
      var self = this;

      this.dotContainers.forEach(function (container) {
        if (container.children.length === wanted) return;
        container.textContent = '';

        for (var i = 0; i < wanted; i++) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'dac-carousel__dot';
          dot.setAttribute('data-dac-car-dot', String(i));
          dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          dot.addEventListener(
            'click',
            (function (target) {
              return function (event) {
                event.preventDefault();
                self.pauseAutoplayForInteraction();
                self.goTo(target);
              };
            })(i)
          );
          container.appendChild(dot);
        }
      });
    }

    /*
      Linked panels: text (or anything else) that lives outside the carousel and
      swaps with the active slide — the pattern where a stack of cards sits on
      one side and its description on the other.

      Every element with the configured class is a panel group, and that group's
      element children are the panels, paired with the slides by order.
    */
    /*
      Groups are looked up lazily and re-queried while none have been found, so
      a panel group rendered by a later section still gets picked up.
    */
    resolvePanelGroups() {
      if (!this.panelClass) return [];
      if (this.panelGroups.length) return this.panelGroups;

      var name = window.CSS && CSS.escape ? CSS.escape(this.panelClass) : this.panelClass;
      try {
        this.panelGroups = Array.prototype.slice.call(document.querySelectorAll('.' + name));
      } catch (e) {
        // An unusable class name would throw on every index change otherwise.
        this.panelClass = '';
      }
      return this.panelGroups;
    }

    /*
      A group's element children are its panels — except for any nav host
      mountNav() added, which would otherwise be paired with a slide and take
      the last panel out of reach.
    */
    panelsIn(group) {
      return Array.prototype.filter.call(group.children, function (el) {
        return el.nodeType === 1 && !el.classList.contains('dac-carousel--nav-host');
      });
    }

    syncPanels() {
      var groups = this.resolvePanelGroups();
      if (!groups.length) return;

      var index = this.index;
      var self = this;
      groups.forEach(function (group) {
        var panels = self.panelsIn(group);
        panels.forEach(function (panel, i) {
          var active = i === index;
          panel.classList.toggle('dac-carousel-panel-hidden', !active);
          // Keep the hidden panels out of the reading order as well as out of view.
          if (active) {
            panel.removeAttribute('aria-hidden');
            panel.inert = false;
          } else {
            panel.setAttribute('aria-hidden', 'true');
            panel.inert = true;
          }
        });
      });
    }

    updateControls() {
      this.syncPanels();

      var index = this.index;
      var last = this.lastIndex();

      this.arrowButtons.forEach(function (button) {
        if (this.loop) {
          button.disabled = false;
          return;
        }
        if (button.hasAttribute('data-dac-car-prev')) button.disabled = index <= 0;
        else button.disabled = index >= last;
      }, this);

      var visibleDots = this.visibleDotRange(last + 1);
      this.dotContainers.forEach(function (container) {
        Array.prototype.forEach.call(container.children, function (dot, i) {
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
          dot.hidden = i < visibleDots.first || i > visibleDots.last;
        });
      });
    }

    /*
      With "Maximum dots" set and more pages than that, only a window of dots
      shows. The window keeps the active dot in its middle where it can, and
      stops at either end, so the dots stay a hint of "more to swipe" without
      wrapping onto extra rows.
    */
    visibleDotRange(totalDots) {
      if (!this.maxDots || totalDots <= this.maxDots) {
        return { first: 0, last: totalDots - 1 };
      }
      var halfWindow = Math.floor(this.maxDots / 2);
      var firstDot = clamp(this.index - halfWindow, 0, totalDots - this.maxDots);
      return { first: firstDot, last: firstDot + this.maxDots - 1 };
    }

    /* --------------------------------------------------------- autoplay */

    startAutoplay() {
      if (!this.autoplay) return;
      this.stopAutoplay();
      var self = this;
      this.autoplayTimer = setInterval(function () {
        if (document.hidden) return;
        // Without looping, stop once the end is reached rather than snapping back.
        if (!self.loop && self.index >= self.lastIndex()) {
          self.stopAutoplay();
          return;
        }
        self.next();
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    // Any deliberate interaction ends autoplay for the rest of the visit —
    // fighting a visitor for control of the slider is worse than stopping.
    pauseAutoplayForInteraction() {
      this.autoplay = false;
      this.stopAutoplay();
    }
  }

  customElements.define('dac-carousel', DacCarousel);
})();
